import { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { t } from 'ttag';
import { ErrorMessages } from 'i18n/messages.js';
import { notify } from 'store/ducks/layout.js';
import PageTitle from 'components/PageTitle/index.js';
import PageDescription from 'components/PageDescription/index.js';
import Input from 'components/Input/index.js';
import ELVStationListMap, { SiteListContext } from 'components/ELVStationListMap/index.js';
import {
    useGetPublicMakesQuery,
    useLazyGetPublicMakeModelsQuery,
    useGetPublicShopsMutation,
    useLazyGetPublicMakeFuzzyQuery,
    useLazyGetPublicMakeModelFuzzyQuery,
} from 'api/index.js';
import './index.scss';

const ListItemCount = () => {
    const site = useContext(SiteListContext);
    return <span className='list-item-count'>({site.vehicle_recycled_count})</span>;
};

const WORD_SEPARATORS = Object.freeze([' ', '-', '/']);
const R_DIGIT = /\d/;
const MS_THROTTLE = 2 ** 10;

// This can only change case, not actual letters!!
const formatMakeOrModel = make => {
    let result = `${make[0].toUpperCase()}${make.slice(1).toLowerCase()}`;

    if(result === 'Kia') return result; // 'Kia' is an exception, other < 4 letter names should be capitalized

    WORD_SEPARATORS.forEach(separator => result = result
        .split(separator)
        .map(word => {
            const isShort = word.length < 4;
            const hasNum = R_DIGIT.test(word);

            return ((isShort || hasNum) && word !== 'roc') // 'T-ROC' is an exception that should be 'T-Roc' instead
                ? word.toUpperCase()
                : `${word[0].toUpperCase()}${word.slice(1)}`;
        })
        .join(separator),
    );

    return result;
};

const SpareParts = () => {
    const dispatch = useDispatch();
    const [getPublicMakeModels] = useLazyGetPublicMakeModelsQuery();
    const [getPublicShops] = useGetPublicShopsMutation();
    const [getPublicMakeFuzzy] = useLazyGetPublicMakeFuzzyQuery();
    const [getPublicMakeModelFuzzy] = useLazyGetPublicMakeModelFuzzyQuery();

    const { data: rawMakes = [] } = useGetPublicMakesQuery();
    const upperMakes = rawMakes.map(m => m.toUpperCase());
    const formattedMakes = rawMakes.map(formatMakeOrModel);
    const [writtenMake, setWrittenMake] = useState('');
    const [usedMake, setUsedMake] = useState('');

    const [rawModels, setRawModels] = useState([]);
    const upperModels = rawModels.map(m => m.toUpperCase());
    const formattedModels = rawModels.map(formatMakeOrModel);
    const [writtenModel, setWrittenModel] = useState('');
    const [usedModel, setUsedModel] = useState('');

    const [writtenYear, setWrittenYear] = useState('');

    const [sites, setSites] = useState([]);
    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const [count, setCount] = useState(0);
    const [errors, setErrors] = useState({});

    const refThrottleFuzzyMake = useRef(null);
    const refThrottleFuzzyModel = useRef(null);

    useEffect(() => {
        usedMake && !errors.make && getPublicMakeModels({ make: usedMake })
            .then(({ data }) => setRawModels(data.map(formatMakeOrModel)));
    }, [usedMake]);

    useEffect(() => { setCount(sites.reduce((acc, cur) => acc + (cur.vehicle_recycled_count || 0), 0)); }, [sites]);

    const onChangeMake = e => {
        const { value } = e.target;

        setWrittenMake(value);
        setUsedMake('');
        setErrors({ ...errors, make: null, model: null });

        setRawModels([]);
        setWrittenModel('');
        setUsedModel('');
        setSites([]);

        clearTimeout(refThrottleFuzzyMake.current);

        if(!value) return;

        const valueUpper = value.toUpperCase();
        const match = upperMakes.find(m => m === valueUpper);
        if(match) {
            setUsedMake(match);
            return;
        }

        refThrottleFuzzyMake.current = setTimeout(() => {
            getPublicMakeFuzzy({ make: value }).then(({ data }) => {
                if(data?.length) {
                    setErrors({ ...errors, make: null });
                    setWrittenMake(formatMakeOrModel(data[0]));
                    setUsedMake(data[0]);
                } else setErrors({ ...errors, make: t`(FORM_VALIDATION_LABEL)_INVALID_MAKE` });
            });
        }, MS_THROTTLE);
    };

    const onChangeModel = e => {
        const { value } = e.target;

        setWrittenModel(value);
        setUsedModel('');
        setErrors({ ...errors, make: null, model: null });
        setSites([]);

        clearTimeout(refThrottleFuzzyModel.current);

        if(!value) return;

        const valueUpper = value.toUpperCase();
        const match = upperModels.find(m => m === valueUpper);
        if(match) {
            setUsedModel(match);
            return;
        }

        refThrottleFuzzyModel.current = setTimeout(() => {
            getPublicMakeModelFuzzy({ make: usedMake, model: value }).then(({ data }) => {
                if(data?.length) {
                    setErrors({ ...errors, model: null });
                    setWrittenModel(formatMakeOrModel(data[0]));
                    setUsedModel(data[0]);
                } else setErrors({ ...errors, model: t`(FORM_VALIDATION_LABEL)_INVALID_MODEL` });
            });
        }, MS_THROTTLE);
    };

    const onChangeYear = e => {
        setSites([]);
        setWrittenYear(e.target.value);
    };

    const onSubmit = e => {
        e.preventDefault();

        getPublicShops({ make: usedMake, model: usedModel, year: writtenYear }).then(({ data, error }) => {
            if(error) return dispatch(notify({ text: ErrorMessages()[error.data.code], type: 'error' }));

            setSites(data);
            !data.length && dispatch(notify({ text: t`(TOP_NOTIFICATION)_PART_FORM_NO_SITES_FOUND`, type: 'warning' }));
        });
    };

    const disabled = errors.make || errors.model || writtenYear.length !== 4;

    return (
        <main id='spare-parts' className={isMapExpanded ? 'is-map-expanded' : ''}>
            <div className='form-container'>
                <PageTitle label={t`(VIEW_TITLE)_UNAUTHED_PARTS_FORM`} />
                <PageDescription label={t`(UNAUTHED_PARTS_FORM)_BODY`} />
                <form id='spare-parts-form' onSubmit={onSubmit}>
                    <Input
                        required
                        name='make'
                        value={writtenMake}
                        onChange={onChangeMake}
                        options={formattedMakes}
                        label={t`(INPUT_LABEL)_MAKE`}
                        placeholder={t`(INPUT_PLACEHOLDER)_MAKE`}
                        maxLength='512'
                        errors={errors}
                    />
                    <Input
                        required
                        name='model'
                        value={writtenModel}
                        onChange={onChangeModel}
                        options={formattedModels}
                        label={t`(INPUT_LABEL)_MODEL`}
                        placeholder={t`(INPUT_PLACEHOLDER)_MODEL`}
                        disabled={!writtenMake || errors.make || !formattedModels?.length}
                        maxLength='512'
                        errors={errors}
                    />
                    <Input
                        required
                        type='number'
                        name='year'
                        min='1769'
                        value={writtenYear}
                        onChange={onChangeYear}
                        label={t`(INPUT_LABEL)_MODEL_YEAR`}
                        placeholder={t`(INPUT_PLACEHOLDER)_MODEL_YEAR`}
                        inputMode='numeric'
                        role='spinbutton'
                        pattern='\d{4}'
                        maxLength='4'
                    />
                </form>
            </div>
            <ELVStationListMap
                sites={sites}
                onClickExpand={setIsMapExpanded}
                listItemHeaderChildren={<ListItemCount />}
            >
                {!!sites.length && <span className='count'>{t`(UNAUTHED_PARTS_FORM)_TOTAL_RECYCLED_${count}`}</span>}
            </ELVStationListMap>
            <input
                type='submit'
                form='spare-parts-form'
                value={t`(UNAUTHED_PARTS_FORM)_BTN_SUBMIT_LABEL`}
                disabled={disabled}
                className='btn--green-s'
            />
        </main>
    );
};

export default SpareParts;
